import { DateTime } from "luxon";
import type { Granularity, RangeKey, RangeSelection, UsageRangeParams } from "./usage.types";

// The lower bound for EVERY date affordance in the picker, not just typed custom ranges:
// it also drives the "All time" preset's start, the calendar's disabled-before-floor cells
// and month-nav clamp, the year dropdown's first year, and the date inputs' `min`.
// COUPLING (un-checked here): this must stay aligned with the backend floor that VALIDATES
// custom ranges — API-Server usageRoutes.ts DATA_FLOOR_ISO (the constant that 400s a
// below-floor start). usageWindows.ts ALL_FLOOR_ISO (the "all" preset's window start) must
// also match it. No shared contract enforces this, so a change on any side silently drifts;
// a backend test asserts the two BE constants agree. Verify all three when touching the floor.
export const DATA_FLOOR_ISO = "2023-10-01";

export type ResolvedRange = { start: DateTime; end: DateTime };
export type QuickItem = {
    key: string;
    label: string;
    presetKey?: RangeKey; // set → emit as a server-resolved preset; unset → compute custom dates
    resolve: (now: DateTime) => ResolvedRange;
};

// Clinic-local "now". Luxon (via ICU) resolves most zone strings — including bare TZ
// abbreviations like "CST"/"PST", which canonicalize to real IANA zones — but a genuinely
// malformed or unsupported string yields an INVALID DateTime whose toISODate() is "", which
// the api layer would then drop, silently degrading to the backend's default window. Fall
// back to local time so `now` (and everything derived from it) is always valid. (EN-854 H1)
export function nowInZone(zone?: string): DateTime {
    const z = zone ? DateTime.now().setZone(zone) : DateTime.now();
    return z.isValid ? z : DateTime.now();
}

// True only for a non-empty, parseable ISO date. The api layer uses this to REJECT a custom
// selection with a missing/invalid endpoint rather than dropping it (which would fall back to
// the default window — plausible-but-wrong data, silently). (EN-854 H1)
export function isValidRangeDate(s: string): boolean {
    return !!s && DateTime.fromISO(s).isValid;
}

// Map a RangeSelection to the exact endpoint params: presets → server-resolved `range`,
// custom → explicit `start`/`end`. Single source for both dashboards (was duplicated inline
// ternaries in UsagePage/CorporatePage). Returns the union type, so an illegal
// neither/both/half-specified shape can't be constructed. (EN-854 I4)
export function selectionToParams(selection: RangeSelection, granularity?: Granularity): UsageRangeParams {
    return selection.kind === "preset"
        ? { range: selection.key, granularity }
        : { start: selection.start, end: selection.end, granularity };
}

// Build the wire search params for a usage request, throwing on a custom range whose
// endpoints are missing/invalid. Shared by usageApi + groupUsageApi so both reject
// (React Query isError → ErrorBanner) instead of silently omitting the window. (EN-854 H1/I4)
export function usageRangeSearchParams(params: UsageRangeParams): Record<string, string> {
    const searchParams: Record<string, string> = {};
    if ("range" in params) {
        searchParams.range = params.range;
    } else {
        if (!isValidRangeDate(params.start) || !isValidRangeDate(params.end)) {
            throw new Error(`Invalid custom usage range: start=${params.start} end=${params.end}`);
        }
        searchParams.start = params.start;
        searchParams.end = params.end;
    }
    if (params.granularity) searchParams.granularity = params.granularity;
    return searchParams;
}

export function isoDate(d: DateTime): string {
    return d.toISODate() ?? "";
}

export function fmtRange(start: DateTime, end: DateTime): string {
    const sameYear = start.year === end.year;
    return `${start.toFormat(sameYear ? "MMM d" : "MMM d, yyyy")} – ${end.toFormat("MMM d, yyyy")}`;
}

export function autoGranularity(startISO: string, endISO: string): Granularity {
    const days = Math.max(1, Math.round(DateTime.fromISO(endISO).diff(DateTime.fromISO(startISO), "days").days) + 1);
    if (days <= 31) return "day";
    if (days <= 120) return "week";
    if (days <= 730) return "month";
    return "quarter";
}

// Rebuild the FULL, gap-free bucket axis for a window. Both usage RPCs only emit a row for a
// day/week/month/quarter that actually had records, so a sparse series (e.g. a low-volume clinic
// with visits on a handful of days) collapses to a few evenly-spaced bars that read as a COARSER
// granularity — "by day" looks weekly. Enumerating every bucket in the window and zero-filling the
// gaps makes the axis unambiguous. Buckets are date_trunc(granularity)-aligned in the clinic zone
// (luxon startOf: week=Monday, quarter=first day — matching Postgres date_trunc) and returned as
// yyyy-MM-dd strings. `endISO` is EXCLUSIVE, mirroring the RPC's `< p_end`. Returns [] on invalid
// input so a bad window degrades to the series-only axis rather than throwing.
export function enumerateBuckets(startISO: string, endISO: string, g: Granularity, zone?: string): string[] {
    const z = zone || undefined;
    const start = DateTime.fromISO(startISO, { zone: z });
    const end = DateTime.fromISO(endISO, { zone: z });
    if (!start.isValid || !end.isValid) return [];
    const step =
        g === "day" ? { days: 1 } : g === "week" ? { weeks: 1 } : g === "month" ? { months: 1 } : { quarters: 1 };
    const out: string[] = [];
    let cur = start.startOf(g);
    // Cap the walk so an inverted/absurd window can't spin (≈ a decade of daily buckets).
    for (let i = 0; cur < end && i < 4000; i++) {
        const iso = cur.toISODate();
        if (iso) out.push(iso);
        cur = cur.plus(step);
    }
    return out;
}

// Resolve a RangeSelection to a concrete [startISO, endISO) window for the chart's zero-fill axis.
// Custom selections carry their own ISO dates; presets resolve via the same quick-group resolver the
// picker uses (clinic-local `now`). The end is EXCLUSIVE (next day) so it composes with
// enumerateBuckets and includes the selected end / today. Returns null if a preset key has no match.
export function resolveSelectionWindow(
    selection: RangeSelection,
    zone?: string,
): { startISO: string; endISO: string } | null {
    if (selection.kind === "custom") {
        const end = DateTime.fromISO(selection.end, { zone: zone || undefined });
        return { startISO: selection.start, endISO: end.isValid ? isoDate(end.plus({ days: 1 })) : selection.end };
    }
    const item = quickGroups()
        .flatMap((grp) => grp.items)
        .find((i) => i.presetKey === selection.key);
    if (!item) return null;
    const r = item.resolve(nowInZone(zone));
    return { startISO: isoDate(r.start), endISO: isoDate(r.end.plus({ days: 1 })) };
}

// Human-readable label for the selected window, for card subtitles ("Apr 27 – Jul 24, 2026").
// Custom selections already carry a formatted `label`; presets are resolved and formatted the
// same way (inclusive end = the exclusive endISO minus a day). Returns "" if unresolvable.
export function formatSelectionRange(selection: RangeSelection, zone?: string): string {
    if (selection.kind === "custom") return selection.label;
    const win = resolveSelectionWindow(selection, zone);
    if (!win) return "";
    const z = zone || undefined;
    const start = DateTime.fromISO(win.startISO, { zone: z });
    const end = DateTime.fromISO(win.endISO, { zone: z }).minus({ days: 1 });
    return start.isValid && end.isValid ? fmtRange(start, end) : "";
}

// Corporate dashboard zero-fill axis. Unlike the clinic dashboard (whose response carries the exact,
// lag-adjusted window in summary.rangeStart/rangeEnd), the group response has no window bounds — so
// the axis is built from the selection. The group RPC excludes the freshest `reportingLagDays` days
// (2 for presets, 0 for custom ranges, echoed in the response), so pull the window end back by that
// lag: filling those excluded days with zeros would show a phantom "drop to 0" on the most recent
// bars. It does NOT read the series, so a clinic filter can't shorten the axis. Returns [] on a
// preset with no matching window (nothing to plot).
export function groupChartBuckets(
    selection: RangeSelection,
    g: Granularity,
    reportingLagDays: number,
    zone?: string,
    // Optional opt-in trim: when set (all-time only, from the corporate page), drop buckets before
    // this ISO date so the axis starts at the group's first Happy Visit instead of the data floor.
    // Derived from the UNFILTERED response, so an account filter still can't shorten the axis.
    firstDataBucketISO?: string,
): string[] {
    const win = resolveSelectionWindow(selection, zone);
    if (!win) return [];
    const end = DateTime.fromISO(win.endISO, { zone: zone || undefined });
    const effectiveEnd = end.isValid ? isoDate(end.minus({ days: Math.max(0, reportingLagDays) })) : win.endISO;
    const buckets = enumerateBuckets(win.startISO, effectiveEnd, g, zone);
    return firstDataBucketISO ? buckets.filter((b) => b >= firstDataBucketISO) : buckets;
}

// `now` is the clinic-local "today"; all boundaries are start-of-day in that zone.
/**
 * Is the most recent chart bucket an in-progress calendar period (this week / this month /
 * this quarter)? Such a bucket is naturally lower than its neighbors — the period hasn't
 * finished and, with the reporting lag, its freshest days are excluded — so the chart fades it
 * to stop it reading as a decline. Day granularity is never "in progress" (a day bucket is a
 * whole past day). `lastBucketISO` is a date-only string (`YYYY-MM-DD`); comparison is on the
 * UTC calendar day so a viewer's timezone can't flip the result. `now` is injectable for tests.
 * Shared by both the corporate (GroupUsageChart) and individual (UsageStackedChart) charts.
 */
export function lastBucketInProgress(
    lastBucketISO: string | undefined,
    granularity: Granularity,
    now: Date = new Date(),
): boolean {
    if (!lastBucketISO || granularity === "day") return false;
    const start = new Date(lastBucketISO); // date-only ISO parses as UTC midnight
    if (Number.isNaN(start.getTime())) return false;
    // Exclusive end of the bucket's calendar period. Week buckets are date_trunc('week') =
    // Monday, so +7 days lands on the next period start regardless of weekday. Quarter
    // buckets are date_trunc('quarter') = the first day of the quarter, so +3 months lands
    // on the next quarter's start.
    const periodEnd =
        granularity === "week"
            ? start.getTime() + 7 * 86400000
            : granularity === "quarter"
              ? Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, 1)
              : Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1);
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return todayUTC < periodEnd;
}

export function quickGroups(): { title: string; items: QuickItem[] }[] {
    const d0 = (n: DateTime) => n.startOf("day");
    return [
        {
            title: "Presets",
            items: [
                {
                    key: "30d",
                    label: "Last 30 days",
                    presetKey: "30d",
                    resolve: (n) => ({ start: d0(n).minus({ days: 29 }), end: d0(n) }),
                },
                {
                    key: "12w",
                    label: "Last 12 weeks",
                    presetKey: "12w",
                    resolve: (n) => ({ start: d0(n).minus({ weeks: 12 }).plus({ days: 1 }), end: d0(n) }),
                },
                {
                    key: "12mo",
                    label: "Last 12 months",
                    presetKey: "12mo",
                    resolve: (n) => ({ start: d0(n).minus({ months: 12 }).plus({ days: 1 }), end: d0(n) }),
                },
                {
                    key: "ytd",
                    label: "Year to date",
                    presetKey: "ytd",
                    resolve: (n) => ({ start: d0(n).startOf("year"), end: d0(n) }),
                },
                {
                    key: "all",
                    label: "All time",
                    presetKey: "all",
                    resolve: (n) => ({
                        start: DateTime.fromISO(DATA_FLOOR_ISO, { zone: n.zoneName ?? undefined }),
                        end: d0(n),
                    }),
                },
            ],
        },
        {
            title: "This / Last",
            items: [
                {
                    key: "thisMonth",
                    label: "This month",
                    resolve: (n) => ({ start: d0(n).startOf("month"), end: d0(n).endOf("month").startOf("day") }),
                },
                {
                    key: "lastMonth",
                    label: "Last month",
                    resolve: (n) => {
                        const m = d0(n).minus({ months: 1 });
                        return { start: m.startOf("month"), end: m.endOf("month").startOf("day") };
                    },
                },
                {
                    key: "thisQuarter",
                    label: "This quarter",
                    resolve: (n) => ({ start: d0(n).startOf("quarter"), end: d0(n).endOf("quarter").startOf("day") }),
                },
                {
                    key: "lastQuarter",
                    label: "Last quarter",
                    resolve: (n) => {
                        const q = d0(n).minus({ quarters: 1 });
                        return { start: q.startOf("quarter"), end: q.endOf("quarter").startOf("day") };
                    },
                },
                {
                    key: "lastYear",
                    label: "Last year",
                    resolve: (n) => {
                        const y = d0(n).minus({ years: 1 });
                        return { start: y.startOf("year"), end: y.endOf("year").startOf("day") };
                    },
                },
            ],
        },
    ];
}
